import { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import * as ROUTES from 'constants/paths';
import { useGetPaymentsHistory } from 'services/payments/payments/hooks';

import {
  ensureGtagInitialized,
  GA_MEASUREMENT_ID,
  trackPurchaseWithGtag,
} from './utils/trackPurchaseWithGtag';

import styles from './SuccessPaymentPage.module.scss';

const REDIRECT_DELAY_SECONDS = 3;
const WORKSPACE_AFTER_PAYMENT = `${ROUTES.WORKSPACE}?newTarif=true`;

const GTAG_INIT_SCRIPT = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', '${GA_MEASUREMENT_ID}');
`;

export const SuccessPaymentPage: FC = () => {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_DELAY_SECONDS);
  const [isReadyToRedirect, setIsReadyToRedirect] = useState(false);

  const { data: paymentData, isFetched } = useGetPaymentsHistory({ page: 0, size: 10, delay: 400 });

  useEffect(() => {
    if (!isFetched) {
      return;
    }

    let cancelled = false;

    ensureGtagInitialized().then(() => {
      if (cancelled) {
        return;
      }

      if (paymentData?.content.length) {
        trackPurchaseWithGtag(paymentData);
      }

      setIsReadyToRedirect(true);
    });

    return () => {
      cancelled = true;
    };
  }, [paymentData, isFetched]);

  useEffect(() => {
    if (!isReadyToRedirect) {
      return;
    }

    setSecondsLeft(REDIRECT_DELAY_SECONDS);

    const countdownInterval = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    const redirectTimeout = window.setTimeout(() => {
      navigate(WORKSPACE_AFTER_PAYMENT, { replace: true });
    }, REDIRECT_DELAY_SECONDS * 1000);

    return () => {
      window.clearInterval(countdownInterval);
      window.clearTimeout(redirectTimeout);
    };
  }, [isReadyToRedirect, navigate]);

  return (
    <>
      <Helmet>
        <title>Thank you - Growexa</title>
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} />
        <script type="text/javascript">{GTAG_INIT_SCRIPT}</script>
      </Helmet>

      <main className={styles.page}>
        <div className={styles.card}>
          <div className={styles.check} aria-hidden="true">
            <svg
              className={styles.checkIcon}
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>

          <h1 className={styles.title}>Thank you for subscribing!</h1>
          <p className={styles.description}>Your payment was successful.</p>
          {isReadyToRedirect && (
            <p className={styles.description}>
              You&apos;ll be redirected to the workspace in <span className={styles.countdown}>{secondsLeft}</span> s.
            </p>
          )}

          <p className={styles.support}>
            Need help?{' '}
            <a className={styles.supportLink} href={`mailto:${ROUTES.CONTACT_US_MAIL}`}>
              Contact support
            </a>
          </p>
        </div>
      </main>
    </>
  );
};
